import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { MainApi } from 'api/MainApi'
import { MainService, OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import FloorMapPreviewOverlay, { FloorMapRobotMarker, FloorMapTrailPoint } from 'components/FloorMapPreviewOverlay'
import Loader from 'components/Loader/Loader'
import { MaintenanceItem, MissionTableRow, MissionsTable } from 'components/MissionsTable/MissionsTable'
import SetPoseConfirmCard from 'components/RobotOnMap/SetPoseConfirmCard/SetPoseConfirmCard'
import { ROBOT_MAP_IMAGES, getRobotIconName } from 'components/RobotOnMapEditable/robotMapUtils'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import Title from 'components/Title/Title'
import { useGausiumLiveExecutionWebSocket } from 'hooks/useGausiumLiveExecutionWebSocket'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRobotSetPoseLocalization } from 'hooks/useRobotSetPoseLocalization'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { GausiumMapGeoPin } from 'pages/Facility/FacilityView/GausiumMapGeoPinsOnMap/GausiumMapGeoPinsOnMap'
import RobotControl from 'pages/Robot/RobotView/RobotLeftBlock/RobotControl/RobotControl'
import RobotAdditionalInfo from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotAdditionalInfo/RobotAdditionalInfo'
import RobotInfo from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotInfo/RobotInfo'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { MissionActions } from 'store/actions/mission'
import { MissionControlActions } from 'store/actions/missionControl'
import { RobotActions } from 'store/actions/robot'
import { selectMissionInfo } from 'store/selectors/mission'
import { selectLoadingProgress } from 'store/selectors/missionControl'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import { GAUSIUM_ENABLED, PROGRESS_TYPES, TIMEOUT_FETCHING_STATUS } from 'utils/constants'
import { PATH } from 'utils/constants'
import { parseMissionControlTab, serializeMissionControlTab } from 'utils/urlViewParams'
import { IMapboxElement, IMission, IRobotInfo, RootState } from 'utils/interfaces'
import { zoomMapTo } from 'utils/mapUtils'
import { isRobotNotFoundError } from 'utils/robotFetchErrors'
import { odysseyStubFromLiveExecution, readLiveExecutionFromRobotRead } from 'utils/robotLiveExecution'
import { SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX, SCENARIO_MAP_COLUMN_INSET_LEFT_PX } from 'utils/scenarioMapLayout'
import { isSetPoseCardVisible } from 'utils/setPoseUi'
import { shouldKeepMissionControl } from 'utils/taskRuntimeState'
import { VENDOR } from 'utils/vendorDisplayName'

import MissionControlMap from './MissionControlMap/MissionControlMap'
import MissionStats from './MissionStats/MissionStats'
import RobotControlButtons from './RobotControlButtons/RobotControlButtons'

import style from './MissionControl.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'

type GausiumMapGeoPinWithVendorMapId = GausiumMapGeoPin & { vendor_map_id?: string; preview_url?: string | null }

/** Sidebar name, facility service points and GeoTIFF come from Redux. Geometries stay in loadedMissions. */
function publishMissionControlMeta(mission: IMission | undefined) {
  if (!mission?.mission) {
    store.dispatch(MissionActions.setMissionInfo(undefined))
    store.dispatch(MissionActions.setGeoTiffLayer(undefined))
    return
  }
  store.dispatch(MissionActions.setMissionInfo(mission.mission))
  store.dispatch(MissionActions.setGeoTiffLayer(mission.geoTiffLayer))
}

const MissionControl = () => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [showSidebar, setShowSidebar] = useState(true)
  const [url, setUrl] = useUrlQueryState({
    tab: {
      parse: parseMissionControlTab,
      serialize: serializeMissionControlTab,
    },
  })
  const tab = url.tab
  const setTab = useCallback((value: number) => setUrl({ tab: value }), [setUrl])
  const params = useParams()
  const navigate = useNavigate()
  const robotId = parseFloat(params.id || '')
  const missionInfo = useSelector(selectMissionInfo)
  const geoTiffShown = useSelector((state: RootState) => state.mission.geoTiffLayer.show)
  const robotInfo = useSelector((state: RootState) => state.robot.robotInfo)
  const localizationMode = useSelector(
    (state: RootState) => state.robot.telemetry?.localization_info?.localization_mode
  )
  const lidarMapName = useSelector(
    (state: RootState) => state.robot.telemetry?.localization_info?.current_lidar_map
  )
  const lidarMapShown = useSelector((state: RootState) => state.robot.lidarMapLayer.show)
  const {
    setPoseMode,
    setSetPoseMode,
    setPosePosition,
    setSetPosePosition,
    setPoseYaw,
    setSetPoseYaw,
    laserscanActive,
    laserscanData,
    toggleLaserscan,
    handleConfirmSetPose,
  } = useRobotSetPoseLocalization({
    robotId: Number.isFinite(robotId) ? robotId : undefined,
    robotSerialNumber: robotInfo?.serialNumber,
    localizationMode,
    lastPosition: robotInfo?.lastPosition,
    lastOrientation: robotInfo?.lastOrientation,
  })

  const loadingProgress = useSelector(selectLoadingProgress)
  const dispatch = useDispatch()
  const [missionId, setMissionId] = useState(0)
  const [initialized, setInitialized] = useState(false)
  const isInitialized = React.useRef(false)
  const isInitializing = React.useRef(false)
  const missionControlPollInFlight = React.useRef(false)
  const currentRobotId = React.useRef<number>(robotId)

  // Local state for scenario missions
  const [loadedMissions, setLoadedMissions] = useState<IMission[]>([])
  const loadedMissionsRef = useRef(loadedMissions)
  loadedMissionsRef.current = loadedMissions
  const [selectedMissionIndices, setSelectedMissionIndices] = useState<Set<number>>(new Set())
  const [highlightedMissionId, setHighlightedMissionId] = useState<number | undefined>(undefined)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeOdyssey, setActiveOdyssey] = useState<OdysseyRead | undefined>(undefined)
  const [scenarioMaintenance, setScenarioMaintenance] = useState<
    { charging?: boolean; washing?: boolean; parking?: boolean } | undefined
  >(undefined)
  const [activeScenario, setActiveScenario] = useState<any | undefined>(undefined)
  const [gausiumMapGeoPins, setGausiumMapGeoPins] = useState<GausiumMapGeoPinWithVendorMapId[]>([])
  const [highlightedGausiumMapId, setHighlightedGausiumMapId] = useState<number | undefined>(undefined)
  const [gausiumPreviewDismissed, setGausiumPreviewDismissed] = useState(false)
  const mapRef = useRef<IMapboxElement>(null)
  const mapWrapperRef = useRef<HTMLDivElement>(null)
  const lastGausiumCameraFocusMapId = useRef<number | undefined>(undefined)

  // Track the current job ID to know when a completely new task/scenario is assigned
  const currentTaskId = React.useRef<number | undefined>(undefined)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  const isGausiumRobot = robotInfo?.vendor === VENDOR.GAUSIUM
  const liveExecution = useMemo(() => {
    const raw = robotInfo?.robotRaw
    return raw ? readLiveExecutionFromRobotRead(raw as any) : undefined
  }, [robotInfo?.robotRaw])
  const { liveExecutionWs, liveExecutionWsIsFresh, liveExecutionWsIntervalMs } = useGausiumLiveExecutionWebSocket(
    Number.isFinite(robotId) ? robotId : null,
    isGausiumRobot && GAUSIUM_ENABLED
  )

  const currentGausiumVendorMapId = useMemo(() => {
    const raw = robotInfo?.robotRaw
    if (!raw) return ''
    const fromWs = liveExecutionWsIsFresh ? String(liveExecutionWs?.map?.external_id || '').trim() : ''
    if (fromWs) return fromWs
    const fromLive = liveExecution?.map?.external_id?.trim()
    if (fromLive) return fromLive
    const lastOdyssey = raw.odysseys?.[Math.max(0, (raw.odysseys?.length || 1) - 1)]
    return String((lastOdyssey?.mission_status_last as any)?.mission_progress?.map_id || '').trim()
  }, [robotInfo?.robotRaw, liveExecution, liveExecutionWs, liveExecutionWsIsFresh])
  const currentLiveExecutionMapId =
    (liveExecutionWsIsFresh ? liveExecutionWs?.map?.id ?? null : null) ?? liveExecution?.map?.id ?? null
  const currentLiveExecutionMapPositionRaw =
    (liveExecutionWsIsFresh ? liveExecutionWs?.map?.position ?? null : null) ?? liveExecution?.map?.position ?? null
  const currentLiveExecutionMapPreviewUrl =
    (liveExecutionWsIsFresh ? liveExecutionWs?.map?.preview_url ?? null : null) ??
    liveExecution?.map?.preview_url ??
    null
  const [stickyLiveMapPosition, setStickyLiveMapPosition] = useState<{ x: number; y: number; angle: number } | null>(
    null
  )
  const [stickyLiveMapId, setStickyLiveMapId] = useState<number | null>(null)

  useEffect(() => {
    if (!currentLiveExecutionMapPositionRaw || currentLiveExecutionMapId == null) return
    setStickyLiveMapPosition(currentLiveExecutionMapPositionRaw)
    setStickyLiveMapId(currentLiveExecutionMapId)
  }, [currentLiveExecutionMapPositionRaw, currentLiveExecutionMapId])

  const prevLocalizationMode = useRef<number | undefined>(undefined)
  const prevLidarMapName = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isGausiumRobot) {
      dispatch(MissionActions.setShowGeoTiffLayer(false))
      dispatch(RobotActions.setShowLidarMap(false))
      return
    }

    const mapName = lidarMapName

    if (localizationMode === undefined) {
      return
    }

    const lidarLocalizationAvailable = localizationMode === 1 || localizationMode === 3
    const lidarMapBecameAvailable = !!mapName && !prevLidarMapName.current
    const lidarMapChanged = !!mapName && !!prevLidarMapName.current && prevLidarMapName.current !== mapName

    if (lidarLocalizationAvailable && mapName) {
      if (
        !lidarMapShown &&
        (prevLocalizationMode.current !== localizationMode || lidarMapBecameAvailable || lidarMapChanged)
      ) {
        dispatch(RobotActions.setShowLidarMap(true))
        dispatch(MissionActions.setShowGeoTiffLayer(false))
      }
    } else if (localizationMode === 2 || !mapName) {
      if (
        (lidarMapShown || !geoTiffShown) &&
        (prevLocalizationMode.current !== localizationMode || !mapName)
      ) {
        dispatch(RobotActions.setShowLidarMap(false))
        dispatch(MissionActions.setShowGeoTiffLayer(true))
      }
    }

    prevLocalizationMode.current = localizationMode
    prevLidarMapName.current = mapName
  }, [dispatch, isGausiumRobot, localizationMode, lidarMapName, lidarMapShown, geoTiffShown])
  const currentLiveExecutionMapPosition =
    currentLiveExecutionMapPositionRaw ??
    (stickyLiveMapPosition && stickyLiveMapId === currentLiveExecutionMapId ? stickyLiveMapPosition : null)

  const currentTrailPoints = useMemo<FloorMapTrailPoint[]>(() => {
    const effectiveState =
      (liveExecutionWsIsFresh
        ? String(liveExecutionWs?.state || '')
            .trim()
            .toUpperCase()
        : '') ||
      String(liveExecution?.state || '')
        .trim()
        .toUpperCase()
    if (effectiveState !== 'RUNNING') return []
    const wsPoints = liveExecutionWsIsFresh ? liveExecutionWs?.trail_points : undefined
    const points = wsPoints ?? liveExecution?.trail_points ?? []
    if (!Array.isArray(points)) return []
    return points
      .map(point => {
        if (!point || typeof point !== 'object') return null
        const x = Number((point as any).x)
        const y = Number((point as any).y)
        if (!Number.isFinite(x) || !Number.isFinite(y)) return null
        return { x, y }
      })
      .filter((point): point is FloorMapTrailPoint => !!point)
  }, [
    liveExecutionWsIsFresh,
    liveExecutionWs?.trail_points,
    liveExecution?.trail_points,
    liveExecutionWs?.state,
    liveExecution?.state,
  ])

  // Lazy floor PNG fetch: cron sync may not have downloaded the preview yet, so the SPA asks
  // the backend to pull it on demand. Cached per map.id so we don't spam the vendor on each poll.
  const [ensuredPreviewByMapId, setEnsuredPreviewByMapId] = useState<Record<number, string | null>>({})
  const [ensureFailedMapIds, setEnsureFailedMapIds] = useState<Set<number>>(new Set())
  // Refs (not state) — flipping them must NOT re-run the lazy-fetch effect (would cancel the
  // in-flight request and discard its result before the .then handler stored it).
  const ensureInFlightMapIdRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isGausiumRobot || !robotInfo?.id) {
      setGausiumMapGeoPins([])
      return
    }
    let cancelled = false
    MainApi.fetchGausiumMaps([robotInfo.id], undefined, 1, 500)
      .then(({ results }) => {
        if (cancelled) return
        setGausiumMapGeoPins(
          (results || []).map(m => ({
            id: m.id,
            name: m.map_name || '',
            geo_point: m.geo_point ?? null,
            vendor_map_id: m.vendor_map_id || '',
            preview_url: m.preview_url ?? null,
          }))
        )
      })
      .catch(() => {
        if (!cancelled) setGausiumMapGeoPins([])
      })
    return () => {
      cancelled = true
    }
  }, [isGausiumRobot, robotInfo?.id])

  useEffect(() => {
    if (!isGausiumRobot) return
    if (tab > 1) setTab(0)
  }, [isGausiumRobot, tab, setTab])

  useEffect(() => {
    setHighlightedGausiumMapId(undefined)
    setGausiumPreviewDismissed(false)
    setEnsuredPreviewByMapId({})
    setEnsureFailedMapIds(new Set())
    ensureInFlightMapIdRef.current = null
    lastGausiumCameraFocusMapId.current = undefined
  }, [robotInfo?.id])

  const currentGausiumMapPk = useMemo(() => {
    if (currentLiveExecutionMapId != null) return currentLiveExecutionMapId
    if (!currentGausiumVendorMapId) return undefined
    return gausiumMapGeoPins.find(p => String(p.vendor_map_id || '').trim() === currentGausiumVendorMapId)?.id
  }, [currentLiveExecutionMapId, currentGausiumVendorMapId, gausiumMapGeoPins])

  // Reset stale dismissal / lazy-fetch flags whenever the robot localizes to a different floor.
  useEffect(() => {
    setGausiumPreviewDismissed(false)
  }, [currentGausiumMapPk])

  /**
   * Effective map id rendered in the overlay:
   * - explicit user pin click takes priority,
   * - otherwise auto-show the current localization map (until the user dismisses with ×).
   */
  const displayedGausiumMapId = highlightedGausiumMapId ?? (gausiumPreviewDismissed ? undefined : currentGausiumMapPk)

  const isShowingCurrentLocalizationMap =
    displayedGausiumMapId != null && currentGausiumMapPk != null && displayedGausiumMapId === currentGausiumMapPk

  const shouldShowUnavailableCurrentMapOverlay = useMemo(() => {
    if (!GAUSIUM_ENABLED || !isGausiumRobot) return false
    if (gausiumPreviewDismissed) return false
    // Show an explicit GS overlay fallback whenever current map preview cannot be rendered:
    // - map is known by vendor id, but we have no local map row/id yet;
    // - map id is missing in telemetry, so current floor map cannot be determined.
    return displayedGausiumMapId == null
  }, [isGausiumRobot, gausiumPreviewDismissed, displayedGausiumMapId])

  const gausiumNoPreviewMessage = useMemo(() => {
    if (!shouldShowUnavailableCurrentMapOverlay) return undefined
    if (!currentGausiumVendorMapId) {
      return t('gausium_map.preview_unavailable_no_map_in_telemetry')
    }
    return t('gausium_map.preview_unavailable')
  }, [shouldShowUnavailableCurrentMapOverlay, currentGausiumVendorMapId, t])

  const ensuredPreviewForDisplayedMap =
    displayedGausiumMapId != null ? ensuredPreviewByMapId[displayedGausiumMapId] : undefined

  const selectedGausiumPreview = useMemo(() => {
    if (displayedGausiumMapId == null) return null
    const pin = gausiumMapGeoPins.find(p => p.id === displayedGausiumMapId)
    let name = String(pin?.name || '')
    // The map list may not include the localization map yet (sync race); fall back to live_execution.
    if (!name && isShowingCurrentLocalizationMap) {
      name = String(liveExecution?.map?.name || '')
    }
    let url: string | null | undefined = pin?.preview_url ?? null
    // For the current map prefer live_execution.map.preview_url (it includes recently re-synced rows)
    // and override with anything we just lazily downloaded.
    if (isShowingCurrentLocalizationMap && currentLiveExecutionMapPreviewUrl) {
      url = currentLiveExecutionMapPreviewUrl
    }
    if (ensuredPreviewForDisplayedMap !== undefined) {
      url = ensuredPreviewForDisplayedMap
    }
    return { url, name }
  }, [
    displayedGausiumMapId,
    gausiumMapGeoPins,
    isShowingCurrentLocalizationMap,
    currentLiveExecutionMapPreviewUrl,
    ensuredPreviewForDisplayedMap,
    liveExecution?.map?.name,
  ])

  useEffect(() => {
    if (highlightedGausiumMapId != null) setGausiumPreviewDismissed(false)
  }, [highlightedGausiumMapId])

  const gausiumPinHighlightId = highlightedGausiumMapId ?? currentGausiumMapPk

  // Lazy preview download for whatever map is currently rendered in the overlay (covers both
  // auto-shown current localization and user-clicked pins). Fires once per map id.
  useEffect(() => {
    if (!GAUSIUM_ENABLED || !isGausiumRobot) return
    const id = displayedGausiumMapId
    if (id == null) return
    if (selectedGausiumPreview?.url) return
    if (ensureFailedMapIds.has(id)) return
    if (id in ensuredPreviewByMapId) return
    if (ensureInFlightMapIdRef.current === id) return
    ensureInFlightMapIdRef.current = id
    MainApi.ensureGausiumMapPreview(id)
      .then(res => {
        setEnsuredPreviewByMapId(prev => ({ ...prev, [id]: res.preview_url ?? null }))
        if (!res.preview_url) {
          setEnsureFailedMapIds(prev => {
            if (prev.has(id)) return prev
            const next = new Set(prev)
            next.add(id)
            return next
          })
        }
      })
      .catch(err => {
        console.warn('[MissionControl] ensureGausiumMapPreview failed', err)
        setEnsuredPreviewByMapId(prev => ({ ...prev, [id]: null }))
        setEnsureFailedMapIds(prev => {
          if (prev.has(id)) return prev
          const next = new Set(prev)
          next.add(id)
          return next
        })
      })
      .finally(() => {
        if (ensureInFlightMapIdRef.current === id) ensureInFlightMapIdRef.current = null
      })
  }, [isGausiumRobot, displayedGausiumMapId, selectedGausiumPreview?.url, ensureFailedMapIds, ensuredPreviewByMapId])

  const gausiumRobotMarker = useMemo<FloorMapRobotMarker | null>(() => {
    if (!isShowingCurrentLocalizationMap) return null
    if (!currentLiveExecutionMapPosition) return null
    if (!robotInfo) return null
    const iconName = getRobotIconName(robotInfo.vendor, robotInfo.robot_color, robotInfo.robot_type)
    const iconUrl = ROBOT_MAP_IMAGES.find(x => x.name === iconName)?.image
    if (!iconUrl) return null
    // Current GS PNGs are not fully unified by "forward" direction:
    // - robot-gs-75 points up (no correction)
    // - robot-gs-40 / robot-gs-omnie / robot-gs-phantas point down (+180 correction)
    const headingOffsetDeg = iconName === 'robot-gs-75' ? 0 : 180
    const currentExecState =
      (liveExecutionWsIsFresh ? liveExecutionWs?.state ?? null : null) ?? liveExecution?.state ?? null
    const defaultTransitionMs = currentExecState === 'RUNNING' ? 1500 : 6000
    const pollElapsedMs = Number(liveExecutionWs?.poll_elapsed_ms)
    const pollElapsedTransitionMs =
      Number.isFinite(pollElapsedMs) && pollElapsedMs > 0 ? Math.max(200, Math.min(8000, pollElapsedMs)) : null
    const transitionMs =
      pollElapsedTransitionMs != null
        ? pollElapsedTransitionMs
        : liveExecutionWsIntervalMs != null && Number.isFinite(liveExecutionWsIntervalMs)
        ? Math.max(200, Math.min(8000, liveExecutionWsIntervalMs))
        : defaultTransitionMs
    return {
      x: currentLiveExecutionMapPosition.x,
      y: currentLiveExecutionMapPosition.y,
      angleDeg: currentLiveExecutionMapPosition.angle,
      iconUrl,
      headingOffsetDeg,
      iconSizePx: 40,
      transitionMs,
    }
  }, [
    isShowingCurrentLocalizationMap,
    currentLiveExecutionMapPosition,
    robotInfo,
    liveExecutionWsIsFresh,
    liveExecutionWs?.state,
    liveExecutionWs?.poll_elapsed_ms,
    liveExecution?.state,
    liveExecutionWsIntervalMs,
  ])

  const overlayLoadingPreview =
    displayedGausiumMapId != null && !selectedGausiumPreview?.url && !ensureFailedMapIds.has(displayedGausiumMapId)
  const gausiumOverlayVisible =
    GAUSIUM_ENABLED &&
    isGausiumRobot &&
    ((displayedGausiumMapId != null && !gausiumPreviewDismissed) || shouldShowUnavailableCurrentMapOverlay)

  const handleGausiumMapPinClick = useCallback(
    (id: number) => {
      if (highlightedGausiumMapId === id) {
        setHighlightedGausiumMapId(undefined)
        setGausiumPreviewDismissed(false)
        setTimeout(() => setHighlightedGausiumMapId(id), 0)
        return
      }
      setHighlightedGausiumMapId(id)
      setGausiumPreviewDismissed(false)
    },
    [highlightedGausiumMapId]
  )

  useEffect(() => {
    if (!GAUSIUM_ENABLED || !isGausiumRobot || highlightedGausiumMapId == null) {
      lastGausiumCameraFocusMapId.current = undefined
      return
    }
    const map = mapRef.current?.getMap()
    if (!map) return
    if (lastGausiumCameraFocusMapId.current === highlightedGausiumMapId) return
    const pin = gausiumMapGeoPins.find(p => p.id === highlightedGausiumMapId)
    const gp = pin?.geo_point
    if (gp?.type !== 'Point' || !gp.coordinates || gp.coordinates.length < 2) return
    const [lng, lat] = gp.coordinates as [number, number]
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) return
    lastGausiumCameraFocusMapId.current = highlightedGausiumMapId
    zoomMapTo(
      map,
      [
        {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: {},
        },
      ],
      SCENARIO_MAP_COLUMN_INSET_LEFT_PX,
      17
    )
  }, [isGausiumRobot, highlightedGausiumMapId, gausiumMapGeoPins])

  // Load geometry for the current job (scenario or single mission) ONLY ONCE per job
  const loadJobData = async (robotInfo: IRobotInfo) => {
    const job = robotInfo?.activeTask
    if (!job) {
      setLoadedMissions([])
      setActiveScenario(undefined)
      publishMissionControlMeta(undefined)
      return
    }

    try {
      // Find the active odyssey in robotRaw based on the current job ID.
      // Some jobs are simple mission jobs ("Task") and MUST NOT be fetched via /odyssey/{id}.
      let odyssey = robotInfo?.robotRaw?.odysseys?.find(o => o.id === job.id)
      const le = robotInfo?.robotRaw ? readLiveExecutionFromRobotRead(robotInfo.robotRaw as any) : undefined
      const jobTypeStr = String(job?.type ?? robotInfo?.robotRaw?.last_task_type ?? '')
        .trim()
        .toUpperCase()
      const isOdysseyLikeJob =
        jobTypeStr === 'ODYSSEY' ||
        jobTypeStr === 'SCENARIO' ||
        // Backend may send enum-like numeric values as string; 5 is SCENARIO.
        jobTypeStr === '5'
      if (!odyssey && le && job.id === le.client_correlation_id && robotInfo.robotRaw) {
        odyssey = odysseyStubFromLiveExecution(robotInfo.robotRaw as any, le)
      }

      if (!odyssey && isOdysseyLikeJob && job.id > 0) {
        try {
          odyssey = await MainService.mainOdysseyRetrieve(job.id)
        } catch (e) {
          console.warn('[MissionControl] Could not fetch missing odyssey', e)
        }
      }

      setActiveOdyssey(odyssey)

      const scenarioId = (odyssey as any)?.scenario || (job as any)?.scenario
      let mission_ids: number[] = []

      if (scenarioId) {
        try {
          const scenarioData = await MainApi.fetchScenario(scenarioId)
          setActiveScenario(scenarioData)
          if (scenarioData?.maintenance) {
            setScenarioMaintenance(scenarioData.maintenance)
          } else {
            setScenarioMaintenance(undefined)
          }
          // Use the scenario's missions list if available
          if (scenarioData?.missions_ids) {
            mission_ids = scenarioData.missions_ids
          }
        } catch (e) {
          console.warn('[MissionControl] Could not fetch scenario data', e)
        }
      } else {
        setActiveScenario(undefined)
        setScenarioMaintenance(undefined)
      }

      // Fallback to odyssey missions if scenario didn't provide any
      if (mission_ids.length === 0) {
        mission_ids = odyssey?.missions || []
      }

      if (mission_ids.length > 0) {
        // Fetch all geometries defined in the odyssey's missions array
        let missions = await Promise.all(mission_ids.map(id => MainApi.fetchMission(id)))
        let active = missions.find(m => m.mission?.id === job.missionId)
        if (!active && job.missionId) {
          active = await MainApi.fetchMission(job.missionId)
          missions = [...missions, active]
        }
        setLoadedMissions(missions)
        loadedMissionsRef.current = missions

        // If it's a huge scenario, optionally could default to visible or not, but for now select all
        setSelectedMissionIndices(new Set(missions.map((_, i) => i)))
        publishMissionControlMeta(active ?? missions[0])
      } else if (job.missionId) {
        const fetchedMission = await MainApi.fetchMission(job.missionId)
        setLoadedMissions([fetchedMission])
        loadedMissionsRef.current = [fetchedMission]
        setSelectedMissionIndices(new Set([0]))
        publishMissionControlMeta(fetchedMission)
      } else {
        setLoadedMissions([])
        loadedMissionsRef.current = []
        publishMissionControlMeta(undefined)
      }
    } catch (error) {
      console.error('[MissionControl] Error fetching job geometries:', error)
    }
  }

  // Initial data loading and handling robotId changes
  useEffect(() => {
    // If robotId changed, reset initialization flags
    if (currentRobotId.current !== robotId) {
      isInitialized.current = false
      isInitializing.current = false
      currentRobotId.current = robotId
      setMissionId(0)
      setInitialized(false)
    }

    // Initialize only if not yet done and not in progress
    if (!isInitialized.current && !isInitializing.current) {
      isInitializing.current = true
      const initialize = async () => {
        try {
          if (!Number.isFinite(robotId) || robotId < 1) {
            navigate(PATH.ASSET_LIST, { replace: true })
            return
          }
          // Fetch robot first
          await store.dispatch(robotThunks.fetchRobot(robotId))
          const fetchedRobot = store.getState().robot.robotInfo
          if (fetchedRobot && !shouldKeepMissionControl(fetchedRobot)) {
            navigate(`${PATH.ROBOT}${robotId}`)
            return
          }
          const mission_id = fetchedRobot?.activeTask?.missionId || 0
          if (fetchedRobot?.activeTask) {
            currentTaskId.current = fetchedRobot.activeTask.id
            await loadJobData(fetchedRobot)
          } else {
            publishMissionControlMeta(undefined)
          }
          if (mission_id) setMissionId(mission_id)

          // Set flags only after data loading is complete (setInitialized triggers re-render so polling effect runs)
          isInitialized.current = true
          setInitialized(true)
        } catch (error) {
          // On error, reset flag to allow retry
          isInitialized.current = false
          setInitialized(false)
          if (isRobotNotFoundError(error)) {
            navigate(PATH.ASSET_LIST, { replace: true })
          }
        } finally {
          isInitializing.current = false
        }
      }
      initialize()
    }
  }, [robotId, navigate])

  useEffect(() => {
    // Don't start interval until initialization is complete
    if (!isInitialized.current || !robotInfo?.id) {
      return
    }

    const polledRobotId = robotInfo.id
    const interval = setInterval(async () => {
      if (missionControlPollInFlight.current) {
        return
      }
      missionControlPollInFlight.current = true
      try {
        // Silent update: do not set loadingProgress so content stays mounted and interval is not cleared
        await store.dispatch(robotThunks.fetchRobotSilent(polledRobotId))
        const updatedRobot = store.getState().robot.robotInfo
        if (updatedRobot && !shouldKeepMissionControl(updatedRobot)) {
          navigate(`${PATH.ROBOT}${polledRobotId}`)
          return
        }
        const newMissionId = updatedRobot?.activeTask?.missionId
        const taskChanged = !!updatedRobot?.activeTask && currentTaskId.current !== updatedRobot.activeTask.id

        if (taskChanged && updatedRobot?.activeTask) {
          currentTaskId.current = updatedRobot.activeTask.id
          await loadJobData(updatedRobot)
        }

        // Same job, next mission in the scenario: geometries are already loaded.
        if (newMissionId && missionId !== newMissionId && newMissionId > 0 && !taskChanged) {
          setMissionId(newMissionId)
          const known = loadedMissionsRef.current.find(m => m.mission?.id === newMissionId)
          publishMissionControlMeta(known ?? (await MainApi.fetchMission(newMissionId)))
        } else if (newMissionId && missionId !== newMissionId) {
          setMissionId(newMissionId)
        }
      } catch (error) {
        // Silently handle network errors (ERR_NETWORK_CHANGED, Failed to fetch, etc.)
        // These are common when network connection is unstable and don't need user attention
        if (error instanceof TypeError && error.message === 'Failed to fetch') {
          return
        }
        console.error('[MissionControl] Error updating robot data:', error)
      } finally {
        missionControlPollInFlight.current = false
      }
    }, TIMEOUT_FETCHING_STATUS)
    return () => {
      clearInterval(interval)
      missionControlPollInFlight.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotInfo?.id, missionId, initialized])

  // Reset loadingProgress on unmount to prevent stale state
  React.useEffect(() => {
    return () => {
      store.dispatch(MissionControlActions.setLoadingProgress(PROGRESS_TYPES.IDLE))
    }
  }, [])

  const filteredMissions = useMemo(() => {
    return loadedMissions.filter((_, i) => selectedMissionIndices.has(i))
  }, [loadedMissions, selectedMissionIndices])

  return (
    <div className={style.container}>
      {loadingProgress === PROGRESS_TYPES.WORK ? (
        <Loader />
      ) : (
        <div className={style.main}>
          <div className={style.titleBlock}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%', pointerEvents: 'auto' }}>
              <Link to={`${PATH.ROBOT}${robotId}`} className={style.robotTitleLink}>
                <Title text={robotInfo?.name || ''} />
              </Link>
              {/* <div style={{ marginLeft: 'auto' }}>
                <RobotTypeLabel vendor={VENDOR.AUTONOMICS} robotType={mission.missionInfo?.robot_type} />
              </div> */}
            </div>
          </div>
          <div className={clsx(style.leftBlock, { [style.hidden]: isMobile && !showSidebar })}>
            <Box className={style.box}>
              <div className={style.mcSidebarHeader}>
                <div className={style.mcTabTitleBlock}></div>
                <div className={style.mcTabButtonsGap}>
                  <RobotControlButtons />
                </div>
              </div>
              <AdaptiveTabs
                value={tab}
                onChange={handleChange}
                indicatorColor="primary"
                sx={{ height: 52, minHeight: 52 }}
              >
                <StyledTab
                  label={t('robot.details')}
                  sx={{
                    fontSize: 16,
                    fontWeight: '400',
                    textTransform: 'none',
                    letterSpacing: 0,
                    padding: '12px 10px',
                  }}
                  disableRipple
                />
                <StyledTab
                  label={t('robot.control')}
                  sx={{
                    fontSize: 16,
                    fontWeight: '400',
                    textTransform: 'none',
                    letterSpacing: 0,
                    padding: '12px 10px',
                  }}
                  disableRipple
                />
                {!isGausiumRobot ? (
                  <StyledTab
                    label={t('mission.details')}
                    sx={{
                      fontSize: 16,
                      fontWeight: '400',
                      textTransform: 'none',
                      letterSpacing: 0,
                      padding: '12px 10px',
                    }}
                    disableRipple
                  />
                ) : null}
              </AdaptiveTabs>
              <TabPanel value={tab} index={0} disablePadding={true} scrollable={true}>
                <MissionStats
                  robot={robotInfo}
                  scenarioId={activeScenario?.id}
                  scenarioName={activeScenario?.name}
                  missionName={missionInfo?.name}
                />
                <RobotInfo />
                <RobotAdditionalInfo />
              </TabPanel>
              <TabPanel value={tab} index={1} disablePadding={true} scrollable={true}>
                <RobotControl />
              </TabPanel>
              {!isGausiumRobot ? (
                <TabPanel value={tab} index={2} disablePadding={true}>
                  <MissionsTable
                    hideActions={true}
                    hideDragging={true}
                    showEquipment={false}
                    allChecked={loadedMissions.length > 0 && selectedMissionIndices.size === loadedMissions.length}
                    onToggleAll={checked => {
                      if (checked) {
                        setSelectedMissionIndices(new Set(loadedMissions.map((_, i) => i)))
                      } else {
                        setSelectedMissionIndices(new Set())
                      }
                    }}
                  >
                    {loadedMissions.map((m, index) => (
                      <MissionTableRow
                        key={`${m.mission?.id}-${index}`}
                        mission={m.mission!}
                        index={index}
                        location={activeScenario?.missions_locations?.[index] ?? m.mission?.location}
                        onClick={() => setHighlightedMissionId(m.mission?.id)}
                        highlighted={highlightedMissionId === m.mission?.id}
                        hideActions={true}
                        hideDragging={true}
                        showEquipment={false}
                        checked={selectedMissionIndices.has(index)}
                        onCheckboxChange={checked => {
                          const newSet = new Set(selectedMissionIndices)
                          if (checked) {
                            newSet.add(index)
                          } else {
                            newSet.delete(index)
                          }
                          setSelectedMissionIndices(newSet)
                        }}
                      />
                    ))}
                    {scenarioMaintenance?.washing && (
                      <MaintenanceItem
                        type="Washing"
                        icon={WashingSvg}
                        hideActions={true}
                        hideDragging={true}
                        showEquipment={false}
                      />
                    )}
                    {scenarioMaintenance?.charging && (
                      <MaintenanceItem
                        type="Charging"
                        icon={ChargingSvg}
                        hideActions={true}
                        hideDragging={true}
                        showEquipment={false}
                      />
                    )}
                    {scenarioMaintenance?.parking && (
                      <MaintenanceItem
                        type="Parking"
                        icon={ParkingSvg}
                        hideActions={true}
                        hideDragging={true}
                        showEquipment={false}
                      />
                    )}
                  </MissionsTable>
                </TabPanel>
              ) : null}
            </Box>
          </div>
          <div className={clsx(style.map, { [style.mapOverlayActive]: gausiumOverlayVisible })} ref={mapWrapperRef}>
            <MissionControlMap
              ref={mapRef}
              missions={filteredMissions}
              hasActiveMission={loadedMissions.length > 0}
              highlightedMissionId={highlightedMissionId}
              isGausium={isGausiumRobot}
              hideRobotIcon={isGausiumRobot}
              gausiumMapPins={gausiumMapGeoPins}
              gausiumPinHighlightedId={gausiumPinHighlightId}
              onGausiumPinClick={GAUSIUM_ENABLED && isGausiumRobot ? handleGausiumMapPinClick : undefined}
              setPoseMode={setPoseMode}
              setSetPoseMode={setSetPoseMode}
              setPosePosition={setPosePosition}
              setPoseYaw={setPoseYaw}
              setSetPosePosition={setSetPosePosition}
              setSetPoseYaw={setSetPoseYaw}
              laserscanActive={laserscanActive}
              laserscanData={laserscanData}
              toggleLaserscan={toggleLaserscan}
            />
            {isSetPoseCardVisible(
              setPoseMode,
              localizationMode,
              laserscanActive
            ) && <SetPoseConfirmCard onCancel={() => setSetPoseMode(false)} onConfirm={handleConfirmSetPose} />}
            {GAUSIUM_ENABLED && isGausiumRobot && (
              <FloorMapPreviewOverlay
                visible={
                  (displayedGausiumMapId != null && !gausiumPreviewDismissed) || shouldShowUnavailableCurrentMapOverlay
                }
                previewUrl={shouldShowUnavailableCurrentMapOverlay ? null : selectedGausiumPreview?.url}
                mapName={undefined}
                mapWidgetRootRef={mapWrapperRef}
                insetLeftPx={isMobile ? 0 : SCENARIO_MAP_COLUMN_INSET_LEFT_PX}
                closeOffsetTopPx={SCENARIO_GS_MAP_PREVIEW_CLOSE_TOP_PX}
                dismissible={false}
                onClose={() => {
                  setHighlightedGausiumMapId(undefined)
                  // Auto-shown current map: × dismisses until the robot localizes elsewhere or
                  // the user clicks a pin again (`gausiumPreviewDismissed` is reset on map id change).
                  if (isShowingCurrentLocalizationMap) setGausiumPreviewDismissed(true)
                }}
                robotMarker={shouldShowUnavailableCurrentMapOverlay ? null : gausiumRobotMarker}
                trailPoints={
                  shouldShowUnavailableCurrentMapOverlay || !isShowingCurrentLocalizationMap ? [] : currentTrailPoints
                }
                loadingPreview={shouldShowUnavailableCurrentMapOverlay ? false : overlayLoadingPreview}
                noPreviewMessage={gausiumNoPreviewMessage}
              />
            )}
          </div>
          {isMobile && (
            <button
              className={MapTableToggleStyle.toggle}
              onClick={() => setShowSidebar((prev: boolean) => !prev)}
              title={showSidebar ? 'Switch to Map' : 'Switch to Table'}
              style={{
                position: 'absolute',
                bottom: '24px',
                right: '24px',
              }}
            >
              {showSidebar ? (
                <MapIcon className={MapTableToggleStyle.icon} />
              ) : (
                <ListAltIcon className={MapTableToggleStyle.icon} />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
export default MissionControl
